import type { BlockConfigFile } from "@/src/models/intake/blocks/block-config";

export type IntakeManagerId = {
  id: number;
  label: string;
};

export const USRC_ADMIT_TYPES = [
  { id: 85, label: "NEW DIALYSIS PATIENT" },
  { id: 88, label: "TRANSFER IN FROM OTHER CENTER" },
  { id: 84, label: "TRANSIENT" },
  { id: 87, label: "INTERNAL TRANSFER" },
  { id: 1456, label: "INTERNAL TRANSIENT" },
  { id: 86, label: "MODALITY CHANGE" },
  { id: 1486, label: "NEW PATIENT – FOREIGN NATIONAL" },
  { id: 1495, label: "RESUME" },
  { id: 1496, label: "OUTGOING TRAVEL" },
  { id: 1497, label: "PATIENT REENGAGEMENT" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_DIAGNOSIS_TYPES = [
  { id: 57, label: "AKI" },
  { id: 58, label: "CHRONIC" },
] as const satisfies readonly IntakeManagerId[];

export const USRC_MODALITY_TYPES = [
  { id: 1448, label: "AKI" },
  { id: 54, label: "CAPD" },
  { id: 55, label: "CCPD" },
  { id: 1416, label: "IN CENTER BACK-UP" },
  { id: 1457, label: "HOME HEMODIALYSIS" },
  { id: 53, label: "INCENTER HEMODIALYSIS" },
  { id: 56, label: "NXSTAGE HEMO" },
  { id: 1417, label: "STAFF ASSIST" },
] as const satisfies readonly IntakeManagerId[];

const CATALOGS_BY_FIELD_SUFFIX: Record<string, readonly IntakeManagerId[]> = {
  admitTypeId: USRC_ADMIT_TYPES,
  diagnosisId: USRC_DIAGNOSIS_TYPES,
  modalityId: USRC_MODALITY_TYPES,
};

export function intakeManagerLabel(rows: readonly IntakeManagerId[], id: number): string {
  const match = rows.find((row) => row.id === id);

  if (!match) {
    throw new Error(`Intake Manager id ${id} is not in the catalog`);
  }

  return match.label;
}

export function applyUsrcIntakeManagerCatalog(config: BlockConfigFile): BlockConfigFile {
  const blocks = { ...config.blocks };

  for (const [key, block] of Object.entries(blocks)) {
    if (!block.fields) continue;

    let changed = false;
    const fields = { ...block.fields };

    for (const [path, field] of Object.entries(fields)) {
      const rows = CATALOGS_BY_FIELD_SUFFIX[path.slice(path.lastIndexOf(".") + 1)];

      if (!rows || field.enabled === false) continue;

      const mappings = rows.map((row) => `${row.id} = ${row.label}`).join("; ");

      fields[path] = {
        ...field,
        description: field.description ? `${mappings}. ${field.description}` : mappings,
        options: rows.map((row) => String(row.id)),
        optionLabels: Object.fromEntries(rows.map((row) => [String(row.id), row.label])),
      };
      changed = true;
    }

    if (changed) blocks[key] = { ...block, fields };
  }

  return { ...config, blocks };
}
